import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Stack, Link } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/AppDownloadLanding.css';

const AppDownloadLanding = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Google Analytics setup
  useEffect(() => {
    // Add Google Analytics script to head
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=AW-17266443177';
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'AW-17266443177');
    `;
    document.head.appendChild(script2);

    // Cleanup function to remove scripts when component unmounts
    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  const screenshots = [
    'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/App%20Landing%20Page/First%20Page/Frame%2029.png',
    'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/App%20Landing%20Page/First%20Page/App%20Section/Frame%2021%20(1).png',
    'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/App%20Landing%20Page/First%20Page/0x0ss%20(1).png',
    'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/App%20Landing%20Page/First%20Page/App%20Section/Frame%2012.png'
  ];

  const getImageClass = (index) => {
    const total = screenshots.length;
    const normalizedIndex = (index + total) % total;
    const diff = (normalizedIndex - currentIndex + total) % total;
    
    if (diff === 0) return 'active';
    if (diff === 1 || diff === -(total - 1)) return 'next';
    if (diff === -1 || diff === (total - 1)) return 'prev';
    if (diff === 2 || diff === -(total - 2)) return 'far-next';
    if (diff === -2 || diff === (total - 2)) return 'far-prev';
    return '';
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % screenshots.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
  };

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
  };

  return (
    <div className="app-download-landing">
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, mt: 0 }}>
            <img 
              src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/App%20Landing%20Page/Yesod%20Logo%204K.png" 
              alt="Yesod Logo" 
              style={{ width: '100px', height: 'auto' }}
            />
          </Box>

          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Candle Lighting Times — As Easy As Checking the Weather
            </Typography>
            <Typography variant="h6" component="h2" sx={{ color: '#666', fontWeight: 'normal', mb: 4 }}>
              Find your local Shabbat and holiday candle lighting times in seconds.
            </Typography>
            <Box sx={{ mb: 4 }}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <a
                  href="https://apps.apple.com/us/app/yesod/id6748472269"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                  onClick={() => {
                    if (window.gtag) {
                      window.gtag('event', 'click', {
                        event_category: 'app_download',
                        event_label: 'candle_lighting_hero_cta'
                      });
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-block',
                      background: 'linear-gradient(135deg, #1A237E 0%, #3F51B5 50%, #5C6BC0 100%)',
                      color: 'white',
                      px: 4,
                      py: 2,
                      borderRadius: '50px',
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      boxShadow: '0 8px 30px rgba(26, 35, 126, 0.3)',
                      border: '2px solid transparent',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 50%, #42A5F5 100%)',
                        boxShadow: '0 12px 40px rgba(26, 35, 126, 0.4)',
                        transform: 'translateY(-2px)'
                      },
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                        transition: 'left 0.5s',
                      },
                      '&:hover:before': {
                        left: '100%'
                      }
                    }}
                  >
                    See Candle Lighting Times
                  </Box>
                </a>
              </motion.div>
            </Box>
          </Box>

          <div 
            className="screenshots-carousel"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="carousel-container">
              {screenshots.map((screenshot, index) => (
                <img
                  key={index}
                  src={screenshot}
                  alt={`App Screenshot ${index + 1}`}
                  className={`app-screenshot ${getImageClass(index)}`}
                  draggable="false"
                />
              ))}
            </div>

            <div className="carousel-dots">
              {screenshots.map((_, index) => (
                <div
                  key={index}
                  className={`dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => handleDotClick(index)}
                />
              ))}
            </div>

            <div className="carousel-arrow prev" onClick={handlePrev}>
              ←
            </div>
            <div className="carousel-arrow next" onClick={handleNext}>
              →
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography sx={{ 
                fontSize: '1.1rem', 
                lineHeight: 1.7, 
                color: '#444',
                maxWidth: '600px',
                mx: 'auto',
                mb: 3
              }}>
                Google results are messy - some are too early, others wildly off. Yesod gives you the exact time based on your location, automatically.
              </Typography>
              <Typography sx={{ 
                fontSize: '1.1rem', 
                lineHeight: 1.7, 
                color: '#444',
                maxWidth: '600px',
                mx: 'auto'
              }}>
                No scrolling. No popups. No guesswork. Just the answer - fast, accurate, and 100% clean.
              </Typography>
            </Box>
          </motion.div>

          {/* Features Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Stack spacing={2} sx={{ maxWidth: '400px', mx: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ color: '#4CAF50', fontSize: '1.2rem' }}>✔️</Box>
                  <Typography sx={{ fontSize: '1.1rem' }}>Auto-detects your location</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ color: '#4CAF50', fontSize: '1.2rem' }}>✔️</Box>
                  <Typography sx={{ fontSize: '1.1rem' }}>Works worldwide</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ color: '#4CAF50', fontSize: '1.2rem' }}>✔️</Box>
                  <Typography sx={{ fontSize: '1.1rem' }}>Clean design, fast load</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ color: '#4CAF50', fontSize: '1.2rem' }}>✔️</Box>
                  <Typography sx={{ fontSize: '1.1rem' }}>Free forever</Typography>
                </Box>
              </Stack>
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Box sx={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
              <motion.a
                href="https://apps.apple.com/us/app/yesod/id6748472269"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  if (window.gtag) {
                    window.gtag('event', 'click', {
                      event_category: 'app_download',
                      event_label: 'candle_lighting_final_cta'
                    });
                  }
                }}
              >
                <img
                  src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/App%20Landing%20Page/appstorenew.png"
                  alt="Download on App Store"
                  style={{ 
                    height: '180px', 
                    width: 'auto',
                    cursor: 'pointer',
                    display: 'block'
                  }}
                />
              </motion.a>
            </Box>
          </motion.div>

          {/* Privacy Policy Link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Box sx={{ textAlign: 'center', mt: 6, mb: 2 }}>
              <Link 
                href="/#/privacy-policy" 
                sx={{ 
                  color: 'rgba(26, 35, 126, 0.7)', 
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  '&:hover': {
                    color: '#1A237E',
                    textDecoration: 'underline'
                  }
                }}
              >
                Privacy Policy
              </Link>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </div>
  );
};

export default AppDownloadLanding; 